<template>
  <v-dialog v-model="open" persistent max-width="440" eager>
    <v-card>
      <v-card-title>Pay with Apple Pay</v-card-title>
      <v-card-text>
        <p>Confirm your payment using the Apple Pay button below.</p>
        <v-progress-linear v-if="!ready" indeterminate class="mb-4"></v-progress-linear>
        <div ref="buttonContainer" :id="mountTargetId" class="apple-pay-button"></div>
        <p v-if="authorizing" class="mt-3 mb-0">Waiting for your payment confirmation…</p>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text :disabled="authorizing" @click="cancel">Cancel</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { loadAirwallexSdk } from '../../../plugins/airwallex-sdk';
import { applePayOptions, supportsAirwallexApplePay } from '../../../plugins/airwallex-apple-pay';

let sequence = 0;
let activeOwner = null;

export default {
  name: 'AirwallexApplePay',
  props: {
    currency: { type: String, required: true },
    label: { type: String, default: 'GOCLOUDEASY PTE. LTD.' },
  },
  data() {
    sequence += 1;
    return {
      open: false,
      ready: false,
      authorizing: false,
      mountTargetId: `airwallex-apple-pay-${sequence}`,
    };
  },
  beforeDestroy() {
    this.finish(new Error('Apple Pay was closed. Please check the payment status before retrying.'));
  },
  methods: {
    confirmIntent(intentId, clientSecret, amount) {
      if (this.$options.applePayAttempt || activeOwner) {
        return Promise.reject(new Error('An Apple Pay payment is already in progress.'));
      }
      if (!supportsAirwallexApplePay(this.currency)) {
        return Promise.reject(new Error('Apple Pay is not available. Please use a credit card.'));
      }
      let options;
      try {
        options = applePayOptions(intentId, clientSecret, amount, this.currency, this.label);
      } catch (error) {
        return Promise.reject(error);
      }
      activeOwner = this;
      this.open = true;
      this.ready = false;
      this.authorizing = false;
      return new Promise((resolve, reject) => {
        const attempt = {
          resolve, reject, sdk: null, listeners: {}, timer: null,
        };
        this.$options.applePayAttempt = attempt;
        this.mountButton(attempt, options);
      });
    },
    async mountButton(pendingAttempt, options) {
      const attempt = pendingAttempt;
      try {
        const sdk = await loadAirwallexSdk();
        await this.$nextTick();
        if (this.$options.applePayAttempt !== attempt) return;
        attempt.sdk = sdk;
        const container = this.$refs.buttonContainer;
        if (!container) throw new Error('Apple Pay could not be opened. Please try again.');
        attempt.container = container;
        const finish = (error, result) => {
          if (this.$options.applePayAttempt === attempt) this.finish(error, result);
        };
        attempt.listeners = {
          onReady: () => {
            if (this.$options.applePayAttempt !== attempt) return;
            this.ready = true;
            clearTimeout(attempt.timer);
          },
          onClick: () => {
            if (this.$options.applePayAttempt !== attempt) return;
            this.authorizing = true;
            clearTimeout(attempt.timer);
            attempt.timer = setTimeout(() => finish(new Error(
              'Apple Pay is still processing. Please check the payment status before retrying.',
            )), 120000);
          },
          onSuccess: (event) => finish(null, event.detail),
          onError: () => finish(new Error('Apple Pay could not complete. Please try again.')),
          onCancel: () => finish(new Error('Apple Pay was cancelled.')),
        };
        Object.entries(attempt.listeners).forEach(([event, listener]) => {
          container.addEventListener(event, listener);
        });
        attempt.timer = setTimeout(() => finish(new Error(
          'Apple Pay could not load. Please use a credit card or try again.',
        )), 20000);
        const element = sdk.createElement('applePayButton', options);
        element.mount(this.mountTargetId);
      } catch (error) {
        if (this.$options.applePayAttempt === attempt) this.finish(error);
      }
    },
    cancel() {
      if (!this.authorizing) this.finish(new Error('Apple Pay was cancelled.'));
    },
    finish(error, result) {
      const attempt = this.$options.applePayAttempt;
      if (!attempt) return;
      this.$options.applePayAttempt = null;
      clearTimeout(attempt.timer);
      if (attempt.container) {
        Object.entries(attempt.listeners).forEach(([event, listener]) => {
          attempt.container.removeEventListener(event, listener);
        });
      }
      if (activeOwner === this) {
        try {
          if (attempt.sdk) attempt.sdk.destroyElement('applePayButton');
        } catch (cleanupError) {
          // Cleanup must not prevent the caller from recovering the payment.
        }
        activeOwner = null;
      }
      this.open = false;
      this.ready = false;
      this.authorizing = false;
      if (error) {
        const paymentError = new Error(error.message || 'Apple Pay could not complete.');
        paymentError.retainAirwallexOperation = true;
        attempt.reject(paymentError);
      } else {
        // The caller verifies the intent with our backend before treating it as paid.
        attempt.resolve(result);
      }
    },
  },
};
</script>

<style scoped>
.apple-pay-button {
  min-height: 44px;
}
</style>
